import * as React from "react";
import { Button, Drawer, Dropdown, Input } from "antd";
import "../../components/design/FolderButton.scss";
import { DsaUploadFolderDto } from "../../generated/dashboard-client";
import FolderIcon from "../icons/FolderIcon";
import MoveFolderIcon from "../icons/MoveFolderIcon";
import { MoreOutlined, DeleteOutlined, EditOutlined, DownOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import useFocus from "../../hooks/useFocus";
import DSAApi from "../../api/DSAApi";

export interface DsaFolderThumb {
  folderId: number;
  thumb: string;
}

export interface DsaFolderButtonProps {
  folder: DsaUploadFolderDto;
  active: boolean;
  onClick: (folder: DsaUploadFolderDto) => void;
  deleteFolder: (folder: DsaUploadFolderDto) => void;
  moveToFolder: (folder: DsaUploadFolderDto) => void;
  folderThumbs: DsaFolderThumb[];
  folderNameExists: (folder: DsaUploadFolderDto, name: string) => boolean | undefined;
  afterFolderRename: (folder: DsaUploadFolderDto, name: string) => void;
  isMobile: boolean;
}

export function DsaFolderButton({
  active,
  folder,
  onClick,
  deleteFolder,
  moveToFolder,
  folderThumbs,
  folderNameExists,
  afterFolderRename,
  isMobile
}: DsaFolderButtonProps) {
  const ref = useRef(null);
  const dropdownButton = useRef<HTMLButtonElement>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [folderThumb, setFolderThumb] = useState("");
  const handleClickOutside = (ev: MouseEvent) => {
    if (!isRenaming) {
      setIsMenuVisible(false);
    }
  };
  useOnClickOutside(ref, (ev) => handleClickOutside(ev as MouseEvent));

  const [renameInputRef, setRenameInputFocus] = useFocus();
  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);

  useEffect(() => {
    setName(folder.name ?? "");
  }, [folder]);

  useEffect(() => {
    const folThumb = folderThumbs.find(thumb => thumb.folderId === folder.id!);
    if (folThumb) {
      setFolderThumb(folThumb.thumb);
    }
  }, [folderThumbs, folder.id]);

  function onClickMenu(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    ev.stopPropagation();
    if (!isMobile) setIsMenuVisible(!isMenuVisible);
    if (isMobile) setIsMobileMenuVisible(!isMobileMenuVisible);
  }

  function onCloseMobileMenu(ev: any) {
    ev.stopPropagation();
    setIsMobileMenuVisible(false);
  }

  function onDeleteClick(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    ev.stopPropagation();
    deleteFolder(folder);
    setIsMenuVisible(false);
  }

  function onMoveToFolderClick(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    ev.stopPropagation();
    moveToFolder(folder);
    setIsMenuVisible(false);
  }

  function rename() {
    setRenaming(false);
    if (!!name) {
      if (folderNameExists(folder, name)) {
        setName(folder.name ?? "");
      } else {
        DSAApi.renameFolder(folder.id!, name)
          .then(() => {
            setName(name);
            afterFolderRename(folder, name);
          })
          .catch(() => {
            setName(folder.name ?? "");
          });
      }
    }
  }

  function startRenaming(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    ev.stopPropagation();
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  function onClickNameInput(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    ev.stopPropagation();
  }

  const menu = (
    <div className={"folder-menu"} ref={ref}>
      <ul>
        <li className="fld-name">
          {!isRenaming && (
            <div className={"edit-name"} onClick={(ev) => startRenaming(ev)}>
              <div>{name || folder.name}</div>
              <Button type={"link"}>
                <EditOutlined />
              </Button>
            </div>
          )}
          <div className={`editing-name ${isRenaming ? "show" : ""}`}>
            <Input
              ref={renameInputRef}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              onBlur={rename}
              onClick={(ev) => onClickNameInput(ev)}
            />
          </div>
        </li>

        <li className="fld-option" onClick={(ev) => onMoveToFolderClick(ev)}>
          <span><MoveFolderIcon /></span>
          <span>Move to folder</span>
        </li>
        <li className="fld-option" onClick={(ev) => onDeleteClick(ev)}>
          <DeleteOutlined />
          <span>Delete folder</span>
        </li>
      </ul>
    </div>
  );

  return (
    <Button
      size={"large"}
      className={`fld-btn ${active ? "active" : ""}`}
      onClick={() => onClick(folder)}
    >
      <div className="fld-thumb">
        {folderThumb &&
          <div
            className={"img-holder"}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "6px",
              backgroundImage: `url(${folderThumb})`,
              backgroundSize: "cover",
              backgroundPosition: "center"
            }}
          />}
        {!folderThumb && <FolderIcon />}
      </div>
      <div className="fld-info">
        <div className={"lb"}>{folder.name}</div>
        <div className={"ct"}>{folder.count} {folder.count === 1 ? "item" : "items"}</div>
      </div>

      {!isMobile && <Dropdown overlay={menu} visible={isMenuVisible} className={"desktop-folder-button"}>
        <Button type={"link"}>
          <MoreOutlined className={"menu-trigger"} ref={dropdownButton} onClick={(ev) => { onClickMenu(ev); }} />
        </Button>
      </Dropdown>}

      {isMobile && <div className={"mobile-folder-button"}>
        <MoreOutlined className={"menu-trigger"} onClick={(ev) => { onClickMenu(ev); }} />
        <Drawer
          placement={"bottom"}
          visible={isMobileMenuVisible}
          onClose={(ev) => onCloseMobileMenu(ev)}
          closeIcon={<DownOutlined />}
          className={"mobile-bottom-menu"}
          height={"180px"}
        >
          {menu}
        </Drawer>
      </div>}
    </Button>
  );
}
